// src/pages/Settings.jsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  alpha,
  Skeleton,
  Checkbox,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Restaurant as RestaurantIcon,
  Print as PrintIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Backup as BackupIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  AdminPanelSettings as AdminIcon,
  PointOfSale as CashierIcon,
  SoupKitchen as ChefIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  LocalAtm as TaxIcon,
  Percent as PercentIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  TableRestaurant as TableRestaurantIcon,
  MenuBook as MenuBookIcon,
  ReceiptLong as ReceiptLongIcon,
  Inventory as InventoryIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Brightness4 as Brightness4Icon,
} from '@mui/icons-material';

// ==================== DETECT MODE ====================
const isElectron = typeof window !== 'undefined' && !!window.electronAPI;
const isBrowser = !isElectron;

console.log('[Settings] Mode:', isElectron ? 'ELECTRON' : 'BROWSER');

// ==================== ALL PAGES & PERMISSIONS ====================
const ALL_PAGES = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { id: 'pos', label: 'POS Billing', icon: <CashierIcon /> },
  { id: 'tables', label: 'Table Management', icon: <TableRestaurantIcon /> },
  { id: 'menu', label: 'Menu Management', icon: <MenuBookIcon /> },
  { id: 'kitchen', label: 'Kitchen Display', icon: <ChefIcon /> },
  { id: 'orders', label: 'Order History', icon: <ReceiptLongIcon /> },
  { id: 'inventory', label: 'Inventory', icon: <InventoryIcon /> },
  { id: 'accounts', label: 'Accounts', icon: <AccountBalanceIcon /> },
  { id: 'reports', label: 'Reports', icon: <AssessmentIcon /> },
  { id: 'staff', label: 'Staff Management', icon: <PeopleIcon /> },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
  { id: 'backup', label: 'Backup', icon: <BackupIcon /> },
];

const ALL_PERMISSIONS = [
  { id: 'create_order', label: 'Create Order' },
  { id: 'edit_order', label: 'Edit Order' },
  { id: 'delete_order', label: 'Delete Order' },
  { id: 'cancel_bill', label: 'Cancel Bill' },
  { id: 'apply_discount', label: 'Apply Discount' },
  { id: 'split_bill', label: 'Split Bill' },
  { id: 'merge_tables', label: 'Merge Tables' },
  { id: 'refund', label: 'Refund' },
  { id: 'manage_menu', label: 'Manage Menu' },
  { id: 'manage_inventory', label: 'Manage Inventory' },
  { id: 'manage_staff', label: 'Manage Staff' },
  { id: 'view_reports', label: 'View Reports' },
  { id: 'manage_settings', label: 'Manage Settings' },
  { id: 'manage_backup', label: 'Manage Backup' },
  { id: 'view_accounts', label: 'View Accounts' },
  { id: 'manage_expenses', label: 'Manage Expenses' },
  { id: 'update_kitchen_status', label: 'Update Kitchen Status' },
  { id: 'view_kot', label: 'View KOT' },
  { id: 'print_bill', label: 'Print Bill' },
  { id: 'print_kot', label: 'Print KOT' },
];

// ==================== STORAGE HELPERS ====================
const STORAGE_KEYS = {
  SETTINGS: 'raath_settings',
  STAFF: 'raath_staff',
  ROLES: 'raath_roles',
};

// Load from localStorage
const loadFromStorage = (key, defaultValue = null) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch (e) {
    console.error(`[Storage] Load ${key} error:`, e);
    return defaultValue;
  }
};

// Save to localStorage
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error(`[Storage] Save ${key} error:`, e);
    return false;
  }
};

// ==================== MAIN COMPONENT ====================
const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // ===== SETTINGS =====
  const [settings, setSettings] = useState({
    restaurant_name: 'My Restaurant',
    address: '',
    phone: '',
    tax_percent: 0,
    service_charge_percent: 0,
    currency_symbol: 'PKR',
    printer_name: '',
    kitchen_printer: '',
    bill_header: '',
    bill_footer: 'Thank you for dining with us!',
    theme_color: '#ff6b35',
    enable_kot_sound: true,
    auto_print_kot: false,
    require_password_for_discount: true,
    enable_dark_mode: false,
  });

  // ===== STAFF =====
  const [staff, setStaff] = useState([]);
  const [staffDialog, setStaffDialog] = useState(false);
  const [staffForm, setStaffForm] = useState({
    name: '',
    role: '',
    phone: '',
    email: '',
    pin_code: '',
    password: '',
    is_active: true,
  });
  const [editingStaff, setEditingStaff] = useState(null);
  const [showPin, setShowPin] = useState(false);

  // ===== ROLES =====
  const [roles, setRoles] = useState([]);
  const [roleDialog, setRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    id: '',
    name: '',
    nameUrdu: '',
    color: '#1976d2',
    description: '',
    pages: [],
    permissions: [],
  });

  // ===== UI STATES =====
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // ==================== LOAD FUNCTIONS ====================
  const loadSettings = () => {
    try {
      const data = loadFromStorage(STORAGE_KEYS.SETTINGS);
      if (data) setSettings(prev => ({ ...prev, ...data }));
    } catch (err) {
      console.error('Settings load error:', err);
    }
  };

  const loadStaff = () => {
    try {
      const data = loadFromStorage(STORAGE_KEYS.STAFF, []);
      if (data && data.length > 0) setStaff(data);
    } catch (err) {
      console.error('Staff load error:', err);
    }
  };

  const loadRoles = () => {
    try {
      const data = loadFromStorage(STORAGE_KEYS.ROLES, []);
      if (data && data.length > 0) setRoles(data);
    } catch (err) {
      console.error('Roles load error:', err);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadSettings();
    loadStaff();
    loadRoles();
    setLoading(false);
  }, []);

  // ==================== SAVE HELPERS ====================
  const saveSettingsToStorage = () => {
    saveToStorage(STORAGE_KEYS.SETTINGS, settings);
  };

  const saveStaffToStorage = (staffData) => {
    saveToStorage(STORAGE_KEYS.STAFF, staffData);
  };

  const saveRolesToStorage = (rolesData) => {
    saveToStorage(STORAGE_KEYS.ROLES, rolesData);
  };

  // ==================== SETTINGS HANDLERS ====================
  const handleSaveSettings = () => {
    saveSettingsToStorage();
    showSnackbar('Settings saved successfully!', 'success');
  };

  // ==================== STAFF HANDLERS ====================
  const handleSaveStaff = () => {
    if (!staffForm.name.trim() || !staffForm.pin_code) {
      showSnackbar('Name and PIN are required', 'warning');
      return;
    }
    if (staffForm.pin_code.length < 4) {
      showSnackbar('PIN must be at least 4 digits', 'warning');
      return;
    }
    
    const roleExists = roles.some(r => r.id === staffForm.role);
    if (!roleExists && staffForm.role) {
      showSnackbar('Selected role does not exist. Please create the role first.', 'warning');
      return;
    }
    
    try {
      let staffData = [...staff];
      
      if (editingStaff) {
        const index = staffData.findIndex(s => s.id === editingStaff.id);
        if (index !== -1) {
          staffData[index] = { ...staffData[index], ...staffForm, id: staffData[index].id };
        }
        showSnackbar('Staff updated successfully!', 'success');
      } else {
        const newStaff = {
          ...staffForm,
          id: Date.now() + Math.floor(Math.random() * 1000),
          created_at: new Date().toISOString()
        };
        staffData.push(newStaff);
        showSnackbar('Staff added successfully!', 'success');
      }
      
      setStaff(staffData);
      saveStaffToStorage(staffData);
      setStaffDialog(false);
      setEditingStaff(null);
      setStaffForm({
        name: '',
        role: '',
        phone: '',
        email: '',
        pin_code: '',
        password: '',
        is_active: true,
      });
    } catch (err) {
      showSnackbar('Failed to save staff: ' + err.message, 'error');
    }
  };

  const openEditStaff = (member) => {
    setEditingStaff(member);
    setStaffForm({
      ...member,
      password: '',
      is_active: member.is_active === 1 || member.is_active === true,
    });
    setStaffDialog(true);
  };

  const handleDeleteStaff = (member) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Staff Member?',
      message: `Are you sure you want to remove "${member.name}"?`,
      onConfirm: () => {
        try {
          const staffData = staff.filter(s => s.id !== member.id);
          setStaff(staffData);
          saveStaffToStorage(staffData);
          showSnackbar('Staff deleted successfully!', 'success');
        } catch (err) {
          showSnackbar('Failed to delete staff', 'error');
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  // ==================== ROLE HANDLERS ====================
  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        id: role.id,
        name: role.name || role.label || '',
        nameUrdu: role.nameUrdu || '',
        color: role.color || '#1976d2',
        description: role.description || '',
        pages: typeof role.pages === 'string' ? JSON.parse(role.pages || '[]') : (role.pages || []),
        permissions: typeof role.permissions === 'string' ? JSON.parse(role.permissions || '[]') : (role.permissions || []),
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        id: '',
        name: '',
        nameUrdu: '',
        color: '#1976d2',
        description: '',
        pages: [],
        permissions: [],
      });
    }
    setRoleDialog(true);
  };

  const handleSaveRole = () => {
    if (!roleForm.id.trim() || !roleForm.name.trim()) {
      showSnackbar('Role ID and Name are required', 'warning');
      return;
    }

    const roleData = {
      id: roleForm.id.toLowerCase().replace(/\s/g, '_'),
      label: roleForm.name,
      name: roleForm.name,
      nameUrdu: roleForm.nameUrdu,
      color: roleForm.color,
      description: roleForm.description,
      pages: roleForm.pages,
      permissions: roleForm.permissions,
    };

    try {
      let rolesData = [...roles];
      
      if (editingRole) {
        const index = rolesData.findIndex(r => r.id === roleData.id);
        if (index !== -1) {
          rolesData[index] = { ...rolesData[index], ...roleData };
        }
        showSnackbar('Role updated successfully!', 'success');
      } else {
        rolesData.push(roleData);
        showSnackbar('Role created successfully!', 'success');
      }
      
      setRoles(rolesData);
      saveRolesToStorage(rolesData);
      setRoleDialog(false);
    } catch (err) {
      showSnackbar('Failed to save role: ' + err.message, 'error');
    }
  };

  const handleDeleteRole = (role) => {
    const inUse = staff.some(s => s.role === role.id);
    if (inUse) {
      showSnackbar('Cannot delete role. It is assigned to staff members.', 'warning');
      return;
    }
    
    setConfirmDialog({
      open: true,
      title: 'Delete Role?',
      message: `Are you sure you want to delete "${role.name}" role?`,
      onConfirm: () => {
        try {
          const rolesData = roles.filter(r => r.id !== role.id);
          setRoles(rolesData);
          saveRolesToStorage(rolesData);
          showSnackbar('Role deleted successfully!', 'success');
        } catch (err) {
          showSnackbar('Failed to delete role', 'error');
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const togglePage = (pageId) => {
    setRoleForm(prev => ({
      ...prev,
      pages: prev.pages.includes(pageId)
        ? prev.pages.filter(p => p !== pageId)
        : [...prev.pages, pageId]
    }));
  };

  const togglePermission = (permId) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const toggleAllPages = () => {
    setRoleForm(prev => {
      const allIds = ALL_PAGES.map(p => p.id);
      const allSelected = allIds.every(id => prev.pages.includes(id));
      return { ...prev, pages: allSelected ? [] : allIds };
    });
  };

  const toggleAllPermissions = () => {
    setRoleForm(prev => {
      const allIds = ALL_PERMISSIONS.map(p => p.id);
      const allSelected = allIds.every(id => prev.permissions.includes(id));
      return { ...prev, permissions: allSelected ? [] : allIds };
    });
  };

  // ==================== BACKUP HANDLERS ====================
  const handleBackup = () => {
    setBackupLoading(true);
    try {
      const data = {
        settings: settings,
        staff: staff,
        roles: roles,
        exportedAt: new Date().toISOString()
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raath_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showSnackbar('Backup downloaded successfully!', 'success');
    } catch (err) {
      showSnackbar('Backup error: ' + err.message, 'error');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestore = () => {
    setConfirmDialog({
      open: true,
      title: 'Restore Data?',
      message: 'This will replace all current data. This action cannot be undone!',
      onConfirm: () => {
        setRestoreLoading(true);
        try {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.json';
          input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (event) => {
              try {
                const data = JSON.parse(event.target.result);
                if (data.settings) {
                  setSettings(data.settings);
                  saveToStorage(STORAGE_KEYS.SETTINGS, data.settings);
                }
                if (data.staff) {
                  setStaff(data.staff);
                  saveToStorage(STORAGE_KEYS.STAFF, data.staff);
                }
                if (data.roles) {
                  setRoles(data.roles);
                  saveToStorage(STORAGE_KEYS.ROLES, data.roles);
                }
                showSnackbar('Data restored successfully!', 'success');
              } catch (err) {
                showSnackbar('Invalid backup file: ' + err.message, 'error');
              }
              setRestoreLoading(false);
            };
            reader.readAsText(file);
          };
          input.click();
        } catch (err) {
          showSnackbar('Restore error: ' + err.message, 'error');
          setRestoreLoading(false);
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // ==================== TABS ====================
  const tabs = [
    { id: 0, label: 'Restaurant', icon: <RestaurantIcon /> },
    { id: 1, label: 'Printers', icon: <PrintIcon /> },
    { id: 2, label: 'Staff', icon: <PeopleIcon /> },
    { id: 3, label: 'Roles', icon: <AdminIcon /> },
    { id: 4, label: 'Backup', icon: <BackupIcon /> },
    { id: 5, label: 'System', icon: <SettingsIcon /> },
  ];

  // ==================== RENDER FUNCTIONS ====================
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: return renderRestaurantInfo();
      case 1: return renderPrinterSettings();
      case 2: return renderStaffList();
      case 3: return renderRoleManagement();
      case 4: return renderBackup();
      case 5: return renderSystem();
      default: return null;
    }
  };

  // ===== TAB 0: RESTAURANT INFO =====
  const renderRestaurantInfo = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
          Restaurant Information
        </Typography>
        
        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Restaurant Name"
              value={settings.restaurant_name}
              onChange={(e) => setSettings({ ...settings, restaurant_name: e.target.value })}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Address"
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={settings.phone}
              onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Bill Header"
              value={settings.bill_header}
              onChange={(e) => setSettings({ ...settings, bill_header: e.target.value })}
              sx={{ mb: 2 }}
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Bill Footer"
              value={settings.bill_footer}
              onChange={(e) => setSettings({ ...settings, bill_footer: e.target.value })}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Tax & Charges Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tax Percentage"
                  value={settings.tax_percent}
                  onChange={(e) => setSettings({ ...settings, tax_percent: parseFloat(e.target.value) || 0 })}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Service Charge"
                  value={settings.service_charge_percent}
                  onChange={(e) => setSettings({ ...settings, service_charge_percent: parseFloat(e.target.value) || 0 })}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Currency Symbol"
                  value={settings.currency_symbol}
                  onChange={(e) => setSettings({ ...settings, currency_symbol: e.target.value })}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
              <Button
                variant="contained"
                size={isMobile ? "medium" : "large"}
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                fullWidth={isMobile}
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2, py: 1.5, fontWeight: 700 }}
              >
                Save Settings
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  // ===== TAB 1: PRINTER SETTINGS =====
  const renderPrinterSettings = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
          Printer Configuration
        </Typography>

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: isMobile ? 48 : 56, height: isMobile ? 48 : 56 }}>
                    <PrintIcon fontSize={isMobile ? "medium" : "large"} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.25rem' }}>Bill Printer</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Main receipt printer</Typography>
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  label="Printer Name"
                  value={settings.printer_name}
                  onChange={(e) => setSettings({ ...settings, printer_name: e.target.value })}
                  sx={{ mb: 2 }}
                  size={isMobile ? "small" : "medium"}
                />
                <FormControlLabel
                  control={<Switch checked={settings.auto_print_kot} onChange={(e) => setSettings({ ...settings, auto_print_kot: e.target.checked })} />}
                  label="Auto-print KOT to Kitchen"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: '#fff3e0', color: '#ed6c02', width: isMobile ? 48 : 56, height: isMobile ? 48 : 56 }}>
                    <ChefIcon fontSize={isMobile ? "medium" : "large"} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.25rem' }}>Kitchen Printer</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>KOT printer in kitchen</Typography>
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  label="Kitchen Printer Name"
                  value={settings.kitchen_printer}
                  onChange={(e) => setSettings({ ...settings, kitchen_printer: e.target.value })}
                  sx={{ mb: 2 }}
                  size={isMobile ? "small" : "medium"}
                />
                <FormControlLabel
                  control={<Switch checked={settings.enable_kot_sound} onChange={(e) => setSettings({ ...settings, enable_kot_sound: e.target.checked })} />}
                  label="Enable KOT notification sound"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  // ===== TAB 2: STAFF LIST =====
  const renderStaffList = () => (
    <Fade in timeout={300}>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 2 : 0 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Staff Members ({staff.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingStaff(null);
              setStaffForm({
                name: '',
                role: roles.length > 0 ? roles[0].id : '',
                phone: '',
                email: '',
                pin_code: '',
                password: '',
                is_active: true,
              });
              setStaffDialog(true);
            }}
            fullWidth={isMobile}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
            disabled={roles.length === 0}
          >
            Add Staff
          </Button>
        </Box>

        {roles.length === 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Please create at least one role first before adding staff.
            <Button size="small" onClick={() => setActiveTab(3)} sx={{ ml: 2 }}>Go to Roles</Button>
          </Alert>
        )}

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.length > 0 ? staff.map((member) => {
                  const roleInfo = roles.find(r => r.id === member.role);
                  const isActive = member.is_active === 1 || member.is_active === true;
                  const color = roleInfo?.color || '#757575';
                  return (
                    <TableRow key={member.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{member.name}</Typography>
                        {member.email && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{member.email}</Typography>}
                      </TableCell>
                      <TableCell>
                        {roleInfo ? (
                          <Chip
                            size="small"
                            label={roleInfo.name}
                            sx={{ bgcolor: `${color}15`, color: color, fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Chip size="small" label="Unknown" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{member.phone || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={isActive ? 'Active' : 'Inactive'}
                          color={isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditStaff(member)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteStaff(member)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>No staff members added yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Fade>
  );

  // ===== TAB 3: ROLE MANAGEMENT =====
  const renderRoleManagement = () => (
    <Fade in timeout={300}>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 2 : 0 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Role Management ({roles.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openRoleDialog()}
            fullWidth={isMobile}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
          >
            Create New Role
          </Button>
        </Box>

        {roles.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2, p: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>No roles created yet.</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Click "Create New Role" to add your first role.
            </Typography>
          </Alert>
        ) : (
          <Grid container spacing={isMobile ? 2 : 3}>
            {roles.map((role) => {
              const inUse = staff.some(s => s.role === role.id);
              return (
                <Grid item xs={12} sm={6} md={4} key={role.id}>
                  <Card sx={{ 
                    borderRadius: 3, 
                    border: `2px solid ${role.color || '#1976d2'}30`,
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                  }}>
                    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: `${role.color || '#1976d2'}15`, color: role.color || '#1976d2', width: 48, height: 48 }}>
                          <AdminIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                            {role.name || role.label}
                          </Typography>
                          {role.nameUrdu && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {role.nameUrdu}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                        {role.description || 'No description'}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          Pages: {role.pages?.length || 0}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {(role.pages || []).slice(0, 4).map((page) => (
                            <Chip key={page} size="small" label={page} sx={{ fontSize: '0.6rem' }} />
                          ))}
                          {(role.pages || []).length > 4 && (
                            <Chip size="small" label={`+${role.pages.length - 4} more`} variant="outlined" sx={{ fontSize: '0.6rem' }} />
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => openRoleDialog(role)}
                          fullWidth
                          sx={{ borderRadius: 2 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteRole(role)}
                          sx={{ borderRadius: 2, minWidth: 40 }}
                          disabled={inUse}
                        >
                          {!isMobile && 'Delete'}
                        </Button>
                      </Box>
                      {inUse && (
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
                          ⚠️ In use by staff
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Role Dialog */}
        <Dialog open={roleDialog} onClose={() => setRoleDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            {editingRole ? 'Edit Role' : 'Create New Role'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Role ID"
                  value={roleForm.id}
                  onChange={(e) => setRoleForm({ ...roleForm, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  helperText="e.g., supervisor, manager"
                  disabled={!!editingRole}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Role Name (Urdu)"
                  value={roleForm.nameUrdu}
                  onChange={(e) => setRoleForm({ ...roleForm, nameUrdu: e.target.value })}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={roleForm.color}
                  onChange={(e) => setRoleForm({ ...roleForm, color: e.target.value })}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  size={isMobile ? "small" : "medium"}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Page Access</Typography>
                  <Button size="small" onClick={toggleAllPages} startIcon={<CheckIcon />}>
                    {ALL_PAGES.every(p => roleForm.pages.includes(p.id)) ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Grid container spacing={1}>
                  {ALL_PAGES.map((page) => (
                    <Grid item xs={6} sm={4} md={3} key={page.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={roleForm.pages.includes(page.id)}
                            onChange={() => togglePage(page.id)}
                            size="small"
                          />
                        }
                        label={page.label}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Permissions</Typography>
                  <Button size="small" onClick={toggleAllPermissions} startIcon={<CheckIcon />}>
                    {ALL_PERMISSIONS.every(p => roleForm.permissions.includes(p.id)) ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Grid container spacing={1}>
                  {ALL_PERMISSIONS.map((perm) => (
                    <Grid item xs={6} sm={4} md={3} key={perm.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={roleForm.permissions.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            size="small"
                          />
                        }
                        label={perm.label}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            <Button onClick={() => setRoleDialog(false)} color="inherit" fullWidth={isMobile}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveRole}
              startIcon={<SaveIcon />}
              fullWidth={isMobile}
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' } }}
            >
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );

  // ===== TAB 4: BACKUP =====
  const renderBackup = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
          Backup & Restore
        </Typography>

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, border: '2px solid #4caf50', bgcolor: (theme) => alpha(theme.palette.success.main, 0.05) }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: '#4caf50', color: '#fff', width: isMobile ? 48 : 64, height: isMobile ? 48 : 64 }}>
                    <DownloadIcon fontSize={isMobile ? "medium" : "large"} />
                  </Avatar>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      Create Backup
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Export all data</Typography>
                  </Box>
                </Box>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2">Complete backup of all data.</Typography>
                </Alert>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={backupLoading ? <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> : <DownloadIcon />}
                  onClick={handleBackup}
                  disabled={backupLoading}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, borderRadius: 2, py: 1.5, fontWeight: 700 }}
                >
                  {backupLoading ? 'Creating...' : 'Create Backup'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, border: '2px solid #f44336', bgcolor: (theme) => alpha(theme.palette.error.main, 0.05) }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: '#f44336', color: '#fff', width: isMobile ? 48 : 64, height: isMobile ? 48 : 64 }}>
                    <UploadIcon fontSize={isMobile ? "medium" : "large"} />
                  </Avatar>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#d32f2f' }}>
                      Restore Data
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Import from backup</Typography>
                  </Box>
                </Box>
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>⚠️ This will replace ALL current data!</Typography>
                </Alert>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={restoreLoading ? <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> : <UploadIcon />}
                  onClick={handleRestore}
                  disabled={restoreLoading}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, borderRadius: 2, py: 1.5, fontWeight: 700 }}
                >
                  {restoreLoading ? 'Restoring...' : 'Restore Data'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  // ===== TAB 5: SYSTEM =====
  const renderSystem = () => (
    <Fade in timeout={300}>
      <Box>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
          System Settings
        </Typography>

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                Security
              </Typography>
              <FormControlLabel
                control={<Switch checked={settings.require_password_for_discount} onChange={(e) => setSettings({ ...settings, require_password_for_discount: e.target.checked })} />}
                label="Require manager PIN for discounts"
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 4, mb: 2 }}>
                Only managers can apply discounts above 10%
              </Typography>
              <FormControlLabel
                control={<Switch checked={settings.enable_dark_mode} onChange={(e) => setSettings({ ...settings, enable_dark_mode: e.target.checked })} />}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Brightness4Icon fontSize="small" /> Enable Dark Mode</Box>}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                <InfoIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'primary.main' }} />
                About
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#ff6b35', width: isMobile ? 48 : 64, height: isMobile ? 48 : 64, fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 700 }}>
                  RD
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? '1rem' : '1.25rem' }}>
                    RAATH DINE
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Restaurant Management System</Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>Version 1.0.0</Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>Total Roles:</strong> {roles.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                <strong>Total Staff:</strong> {staff.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Mode:</strong> {isElectron ? 'Desktop' : 'Browser'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ p: isMobile ? 1.5 : isTablet ? 2 : 3, maxWidth: 1400, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
          Settings
        </Typography>
        <Chip size="small" label={isElectron ? 'Desktop Mode' : 'Browser Mode'} color={isElectron ? 'primary' : 'info'} sx={{ mt: 1 }} />
      </Box>

      {/* Loading State */}
      {loading ? (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Stack>
        </Paper>
      ) : (
        <>
          {/* Tabs */}
          <Paper sx={{ borderRadius: 3, mb: 2, overflow: 'hidden', position: 'sticky', top: 0, zIndex: 10 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, val) => setActiveTab(val)} 
              sx={{ px: isMobile ? 0 : 2, pt: isMobile ? 0.5 : 1 }}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
            >
              {tabs.map((tab) => (
                <Tab 
                  key={tab.id}
                  icon={tab.icon}
                  label={isMobile ? '' : tab.label}
                  iconPosition="start"
                  sx={{
                    '&.Mui-selected': { color: '#ff6b35' },
                    minHeight: isMobile ? 48 : 64,
                    fontSize: isMobile ? '0.7rem' : '0.875rem',
                    py: isMobile ? 1 : 1.5,
                    px: isMobile ? 1 : 2,
                  }}
                />
              ))}
            </Tabs>
          </Paper>

          {/* Content */}
          <Paper sx={{ p: isMobile ? 1.5 : isTablet ? 2 : 3, borderRadius: 3, minHeight: isMobile ? 400 : 500 }}>
            {renderTabContent()}
          </Paper>
        </>
      )}

      {/* Staff Dialog */}
      <Dialog open={staffDialog} onClose={() => setStaffDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          {editingStaff ? 'Edit Staff' : 'Add Staff'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={staffForm.name}
                onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                autoFocus
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Role"
                value={staffForm.role}
                onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                size={isMobile ? "small" : "medium"}
                error={roles.length === 0}
                helperText={roles.length === 0 ? "Create a role first" : ""}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: role.color || '#1976d2' }} />
                      <Typography variant="body2">{role.name || role.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={staffForm.phone}
                onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                required
                value={staffForm.email}
                onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showPin ? 'text' : 'password'}
                label="PIN Code"
                required
                value={staffForm.pin_code}
                onChange={(e) => setStaffForm({ ...staffForm, pin_code: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPin(!showPin)} edge="end" size="small">
                        {showPin ? <ViewIcon /> : <HideIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="4-6 digit PIN for login"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={staffForm.is_active} onChange={(e) => setStaffForm({ ...staffForm, is_active: e.target.checked })} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setStaffDialog(false)} color="inherit" fullWidth={isMobile}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveStaff}
            startIcon={<SaveIcon />}
            fullWidth={isMobile}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' } }}
            disabled={roles.length === 0}
          >
            {editingStaff ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f' }}>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} color="inherit" fullWidth={isMobile}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => confirmDialog.onConfirm?.()} fullWidth={isMobile}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;